<template>
	<Dialog
		v-model:visible="visible"
		:header="$t('Squeeze.Prompts.ImportErrorHeader')"
		:modal="true"
		@hide="onClose"
	>
		<Message severity="warn" :closable="false">
			{{ $t('Squeeze.Prompts.ImportErrorHint') }}
		</Message>

		<DataTable
			:value="errors"
			class="p-datatable-sm"
			:autoLayout="true"
			:scrollable="true"
			scrollHeight="25rem"
			:rowClass="rowClass"
		>
			<Column field="prompt.fieldName" :header="$t('Squeeze.XmlMapper.FieldName')" class="truncated-column" style="min-width: 9rem;">
				<template #body="slotProps">
					<div class="column-cell" :class="{'cell-error': isMissing(slotProps.data, 'fieldName')}" v-tooltip.bottom="getCellTooltip(slotProps.data, 'fieldName', slotProps.data.prompt.fieldName)">
						{{ slotProps.data.prompt.fieldName }}
					</div>
				</template>
			</Column>

			<Column :header="$t('Squeeze.XmlMapper.HeadData') + ' / ' + $t('Squeeze.XmlMapper.PositionData')" style="min-width: 7rem;">
				<template #body="slotProps">
					{{ slotProps.data.prompt.lineItem ? $t('Squeeze.XmlMapper.PositionData') : $t('Squeeze.XmlMapper.HeadData') }}
				</template>
			</Column>

			<Column :header="$t('Squeeze.BatchClasses.BatchClass')" class="truncated-column" style="min-width: 8rem;">
				<template #body="slotProps">
					<div class="column-cell" :class="{'cell-error': isMissing(slotProps.data, 'batchClass')}" v-tooltip.bottom="getCellTooltip(slotProps.data, 'batchClass', slotProps.data.prompt.batchClass)">
						{{ slotProps.data.prompt.batchClass || '*' }}
					</div>
				</template>
			</Column>

			<Column :header="$t('Squeeze.XmlMapper.DocumentClass')" class="truncated-column" style="min-width: 8rem;">
				<template #body="slotProps">
					<div class="column-cell" :class="{'cell-error': isMissing(slotProps.data, 'documentClass')}" v-tooltip.bottom="getCellTooltip(slotProps.data, 'documentClass', slotProps.data.prompt.documentClass)">
						{{ slotProps.data.prompt.documentClass || '*' }}
					</div>
				</template>
			</Column>

			<Column :header="$t('Squeeze.XmlMapper.Company')" style="min-width: 7rem;">
				<template #body="slotProps">
					<div class="column-cell" :class="{'cell-error': isMissing(slotProps.data, 'company')}" v-tooltip.bottom="getCellTooltip(slotProps.data, 'company', slotProps.data.prompt.company)">
						{{ slotProps.data.prompt.company || '*' }}
					</div>
				</template>
			</Column>

			<Column :header="$t('Squeeze.Prompts.Creditor')" style="min-width: 7rem;">
				<template #body="slotProps">
					<div class="column-cell" :class="{'cell-error': isMissing(slotProps.data, 'creditor')}" v-tooltip.bottom="getCellTooltip(slotProps.data, 'creditor', slotProps.data.prompt.creditor)">
						{{ slotProps.data.prompt.creditor || '*' }}
					</div>
				</template>
			</Column>

			<Column :header="$t('Squeeze.Prompts.MissingReferences')" class="references-column" style="min-width: 12rem;">
				<template #body="slotProps">
					<div class="column-cell" :class="{'cell-error': hasReferenceWithoutColumn(slotProps.data)}" v-tooltip.bottom="getMissingReferences(slotProps.data)">
						{{ getMissingReferences(slotProps.data) }}
					</div>
				</template>
			</Column>

			<Column style="min-width: 9rem; max-width: 9rem; text-align: right;">
				<template #body="slotProps">
					<span v-if="isIgnored(slotProps.index)" class="ignored-hint">
						<i class="mdi mdi-check" />&nbsp;{{ $t('Squeeze.Prompts.Ignored') }}
					</span>
					<Button
						v-else
						:label="$t('Squeeze.Prompts.Ignore')"
						icon="mdi mdi-cancel"
						class="p-button-sm p-button-text p-button-danger"
						@click="ignoreError(slotProps.index)"
					/>
				</template>
			</Column>
		</DataTable>

		<template #footer>
			<Button
				:label="$t('Squeeze.Prompts.IgnoreAll')"
				icon="mdi mdi-cancel"
				class="p-button-text"
				:disabled="allErrorsIgnored"
				@click="ignoreAllErrors"
			/>
			<Button
				:label="$t('Squeeze.General.Abort')"
				icon="mdi mdi-close"
				class="p-button-text"
				@click="onClose"
			/>
			<Button
				:label="$t('Squeeze.Prompts.StartImport')"
				icon="mdi mdi-check"
				class="p-button-text"
				:loading="loading"
				:disabled="!allErrorsIgnored"
				@click="onConfirm"
			/>
		</template>
	</Dialog>
</template>

<script lang="ts">
import {computed, defineComponent, PropType, ref, watch} from "vue";
import {useI18n} from "vue-i18n";
import Dialog from "primevue/dialog";
import Message from "primevue/message";
import DataTable from "primevue/datatable";
import Column from "primevue/column";
import Tooltip from "primevue/tooltip";
import {LlmPromptImportError, LlmPromptReference} from "@dex/squeeze-client-ts";

/** References that have their own column and are therefore marked directly at their value */
const REFERENCES_WITH_COLUMN: LlmPromptReference[] = [
	LlmPromptReference.FieldName,
	LlmPromptReference.BatchClass,
	LlmPromptReference.DocumentClass,
	LlmPromptReference.Company,
	LlmPromptReference.Creditor,
];

export default defineComponent({
	name: "PromptImportErrorDialog",
	components: {
		Dialog, Message, DataTable, Column,
	},
	directives: {
		'tooltip': Tooltip,
	},
	props: {
		/** Show the dialog? */
		show: {
			type: Boolean,
			default: false,
		},
		/** Prompts of the import file that cannot be imported */
		errors: {
			type: Array as PropType<LlmPromptImportError[]>,
			default: () => [],
		},
		/** Show a loading indicator on the import button? */
		loading: {
			type: Boolean,
			default: false,
		},
	},
	emits: ["onClose", "onConfirm"],
	setup(props, {emit}) {
		const {t} = useI18n();

		/** Show the dialog? (local copy, because props can't be mutated) */
		const visible = ref<boolean>(props.show);

		/** Indexes of the errors the user decided to ignore */
		const ignoredErrors = ref<number[]>([]);

		/** Has every error been ignored? Only then the import may be started. */
		const allErrorsIgnored = computed(() => props.errors.length > 0 && ignoredErrors.value.length === props.errors.length);

		/** Has the error with the given index been ignored? */
		const isIgnored = (index: number) => ignoredErrors.value.includes(index);

		/** Ignores a single error */
		const ignoreError = (index: number) => {
			if (!isIgnored(index)) {
				ignoredErrors.value = [...ignoredErrors.value, index];
			}
		}

		/** Ignores all remaining errors */
		const ignoreAllErrors = () => {
			ignoredErrors.value = props.errors.map((error: LlmPromptImportError, index: number) => index);
		}

		/** Greys out the rows that have been ignored */
		const rowClass = (data: LlmPromptImportError) => {
			return isIgnored(props.errors.indexOf(data)) ? 'ignored-row' : '';
		}

		/** Is the given reference of a prompt the one that could not be matched? */
		const isMissing = (error: LlmPromptImportError, reference: LlmPromptReference) => {
			return error.missingReferences.includes(reference);
		};

		/**
		 * Tooltip of a single value: names the problem for a value that could not be matched,
		 * otherwise it just shows the full value.
		 * @param error Prompt that cannot be imported
		 * @param reference Reference shown in the cell
		 * @param value Value shown in the cell
		 */
		const getCellTooltip = (error: LlmPromptImportError, reference: LlmPromptReference, value?: string | null) => {
			if (isMissing(error, reference)) {
				const notFound = t('Squeeze.Prompts.ReferenceNotFound', {reference: t('Squeeze.Prompts.References.' + reference)});
				return value ? value + ' – ' + notFound : notFound;
			}

			return value ?? '';
		};

		/**
		 * Has the prompt a missing reference without an own column (data type, prompt text)?
		 * Those are marked at the list of missing references instead.
		 */
		const hasReferenceWithoutColumn = (error: LlmPromptImportError) => {
			return error.missingReferences.some((reference: LlmPromptReference) => !REFERENCES_WITH_COLUMN.includes(reference));
		};

		/** Translated, comma separated list of the references that are missing locally */
		const getMissingReferences = (error: LlmPromptImportError) => {
			return error.missingReferences
				.map((reference: LlmPromptReference) => t('Squeeze.Prompts.References.' + reference))
				.join(', ');
		}

		/** Closes the dialog */
		const onClose = () => {
			visible.value = false;
			emit("onClose");
		}

		/** Starts the import of the resolvable prompts */
		const onConfirm = () => {
			emit("onConfirm");
		}

		/** if error dialog changed visibility, clear ignored errors */
		watch(() => props.show, () => {
			visible.value = props.show;

			if (props.show) {
				ignoredErrors.value = [];
			}
		})

		return {
			visible,
			allErrorsIgnored,
			isIgnored,
			ignoreError,
			ignoreAllErrors,
			rowClass,
			isMissing,
			getCellTooltip,
			hasReferenceWithoutColumn,
			getMissingReferences,
			onClose,
			onConfirm,
		};
	},
});
</script>

<style lang="scss" scoped>

::v-deep(td div.column-cell) {
	white-space: nowrap;
	overflow: hidden;
	text-overflow: ellipsis;
}

::v-deep(tr.ignored-row) {
	opacity: 0.5;
}

// Truncate long values to stop them from stretching the table, the full value stays available as tooltip
.truncated-column ::v-deep(div.column-cell) {
	max-width: 12rem;
}

.references-column ::v-deep(div.column-cell) {
	max-width: 16rem;
}

// Value that could not be matched
.cell-error {
	padding: 0.5rem;
	box-shadow: inset 0 0 0 0.1rem var(--error-color);
	background-color: rgba(255, 0, 0, 0.04);
	border-radius: 0.5rem;
}

.ignored-hint {
	color: var(--text-color-secondary);
	white-space: nowrap;
}

</style>
